import * as rq from '@tanstack/react-query';

import { appConfig } from '@/config';
import { useAuth } from '@/hooks/use-auth';
import { useMe } from '@/hooks/use-me';

import { communicationV1EmailsListRetrieve } from '../../__generated__/oazapfts-client';
import {
  type Communication,
  type CommunicationPagination,
} from '../../entities/communication';
import type { BodyOf } from '../../helpers/body-of';
import { communicationPaginationMapper } from '../../mappers/communication.mapper';

export namespace GetAllCommunications {
  export const key = ({ search, type, siteIds }: Input) => [
    'communication',
    'get-all-communications',
    ...(search || type || (siteIds && siteIds.length > 0)
      ? [{ search, type, siteIds }]
      : []),
  ];

  const $typeFromType: Record<
    Communication['type'],
    BodyOf<typeof communicationV1EmailsListRetrieve>['$type']
  > = {
    email: 'regular',
    meeting: 'meeting',
  };

  export type Input = {
    search?: string;
    type?: Communication['type'];
    siteIds?: string[];
  };
  export type Output = CommunicationPagination;

  export function useInfiniteQuery(input: Input) {
    const auth = useAuth();
    const me = useMe();

    return rq.useInfiniteQuery<Output>({
      queryKey: key(input),
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      keepPreviousData: true,
      async queryFn({ pageParam }) {
        const accessToken = await auth.getAccessToken();
        const result = await communicationV1EmailsListRetrieve(
          {
            $type: input.type ? $typeFromType[input.type] : undefined,
            search: input.search,
            offset: pageParam ?? 0,
            sitesAccureIds:
              input.siteIds && input.siteIds.length > 0
                ? input.siteIds
                : undefined,
            limit: 15,
            xUserName: me.displayName,
            xUserEmail: me.email,
          },
          {
            baseUrl: `${appConfig.bffUrl}/plex`,
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        // @ts-expect-error status can be 204
        if (result.status === 204) {
          return { totalCount: 0, nextCursor: undefined, communications: [] };
        }

        if (result.status !== 200) {
          throw new Error('Fail to fetch communications');
        }

        return communicationPaginationMapper(result.data);
      },
    });
  }
}
